import { useState } from "react"
import Checkbox from "@/v2-ui/checkbox"
import Icon from "@/v2-ui/icon"
import Label from "@/v2-ui/label"
import cn from "@/v2-ui/utils/utils.cn"
import Badge from "@/v2-backoffice-client/components/badge"
import LicenseFeatureGroupDetailsHoverCard from "@/v2-backoffice-client/license/featureGroup/compose/LicenseFeatureGroupDetailsHoverCard"
import LicenseFeatureGroupIncludedMark from "@/v2-backoffice-client/license/featureGroup/compose/LicenseFeatureGroupIncludedMark"
import LicenseFeatureGroupPackageOnlyMark from "@/v2-backoffice-client/license/featureGroup/compose/LicenseFeatureGroupPackageOnlyMark"
import LicenseFeatureGroupReachIndicator from "@/v2-backoffice-client/license/featureGroup/compose/LicenseFeatureGroupReachIndicator"
import LicenseFeatureGroupReachMark from "@/v2-backoffice-client/license/featureGroup/compose/LicenseFeatureGroupReachMark"
import LicenseFeatureGroupTickCheckbox from "@/v2-backoffice-client/license/featureGroup/compose/LicenseFeatureGroupTickCheckbox"
import {
  PENDING_CHANGE_LABEL,
  ROW_NOTE_CLASS,
  WHOLE_MODULE_NOUN,
} from "@/v2-backoffice-client/license/featureGroup/compose/license.featureGroup.compose.constants"
import { tickMeaningOf } from "@/v2-backoffice-client/license/featureGroup/compose/license.featureGroup.placement.utils"
import {
  isPersonLens,
  useFeatureGroupLens,
} from "@/v2-backoffice-client/license/featureGroup/compose/license.featureGroup.lens.context"
import {
  describeIncluded,
  describeReach,
  describeRowStatus,
  isFixedByLens,
  rowStatusOf,
} from "@/v2-backoffice-client/license/featureGroup/compose/license.featureGroup.lens.utils"
import {
  pluralize,
  type FeatureGroupRuleLike,
} from "@/v2-backoffice-client/license/featureGroup/compose/license.featureGroup.compose.utils"
import {
  capabilityFactsOf,
  capabilityGrantOf,
  isLockable,
  kindLabelOf,
  nameOf,
  otherPackagesOf,
  packagesOf,
  pickClosureOf,
  type LicenseFeatureGroupCapability,
  type LicenseFeatureGroupCapabilityTree,
} from "@/v2-backoffice-client/license/featureGroup/compose/license.featureGroup.capability.utils"

export type LicenseFeatureGroupCapabilityRowProps = {
  capability: LicenseFeatureGroupCapability
  tree: LicenseFeatureGroupCapabilityTree
  /** Nesting level; the rows inside this one render one deeper. */
  depth?: number
  /** The package being edited, so "also in" never names it. */
  featureGroupName: string
  rules: readonly FeatureGroupRuleLike[]
  persistedRules: readonly FeatureGroupRuleLike[]
  isPicked(code: string): boolean
  wasPicked(code: string): boolean
  onSetPicked(
    capabilities: readonly LicenseFeatureGroupCapability[],
    picked: boolean,
  ): void
}

// One capability as a checklist row. The rows that sit inside it — a tab's
// sub-tabs and panel modules — are indented beneath, behind a chevron the
// row owns (open by default: the nesting is the information). Ticking the
// row also ticks what it needs to be reached; unticking it unticks what
// sits inside. Under the module's rule the row is ticked and still, "whole
// module", so an operator never wonders why a box will not untick.
//
// Through the package lens the row is the package's membership: the
// checkbox, the package-only badge where the catalog gates the row, the
// other packages carrying it. Through the customer lens the row is the
// ANSWER for that customer: a fixed tick when the product or a license
// already includes it (with "via Placepoint Enterprise" where a license
// does), a checkbox only where this tab decides — ticked "added here",
// empty "needs a package" — and, with a person picked, whether the rule
// reaches them. A surface whose text is not written yet is named off its
// code.
function LicenseFeatureGroupCapabilityRow(
  props: LicenseFeatureGroupCapabilityRowProps,
) {
  const {
    capability,
    tree,
    depth = 0,
    featureGroupName,
    rules,
    persistedRules,
    isPicked,
    wasPicked,
    onSetPicked,
  } = props

  const parts = tree.childrenOf(capability.Code)
  const [isOpen, setIsOpen] = useState(true)

  const grant = capabilityGrantOf(capability, rules, isPicked)
  const picked = grant !== null
  const isByRule = grant === "module"
  const wasGranted =
    capabilityGrantOf(capability, persistedRules, wasPicked) !== null
  const isChanged = picked !== wasGranted
  const lens = useFeatureGroupLens()
  const status = rowStatusOf(
    lens,
    capability,
    grant === "module" ? "rule" : grant === "pick" ? "pick" : null,
  )
  const isFixed = isFixedByLens(status)
  const statusText = status && describeRowStatus(status, WHOLE_MODULE_NOUN)
  const alsoIn =
    lens.kind === "package"
      ? otherPackagesOf(capability, featureGroupName)
      : null
  const reach =
    lens.kind === "customer" && lens.reaches
      ? lens.reaches(capability.Code)
      : undefined
  const parent = tree.parentOf(capability.Code)
  const isOrphaned =
    picked &&
    !isByRule &&
    !!parent &&
    isLockable(parent) &&
    !isPicked(parent.Code)
  const showsKind = lens.kind === "package" && (depth > 0 || parts.length > 0)
  // Through the package lens the checkbox carries what the tick means (a
  // dash for a placement, a tick for access; license.featureGroup.placement.utils).
  const tickMeaning = tickMeaningOf({
    isPackageOnly: isLockable(capability),
    isCarriedHere: picked,
    packageNames: packagesOf(capability),
  })
  const otherPackages = packagesOf(capability).filter(
    (name) => name !== featureGroupName,
  )

  return (
    <>
      <li
        className={cn(
          "flex min-w-0 items-center border-l-2 border-l-transparent pr-2",
          isChanged && "border-l-primary bg-primary/5",
        )}
        style={{ paddingLeft: `${0.5 + depth * 1.25}rem` }}
      >
        <Label
          className={cn(
            "flex min-w-0 flex-1 items-center gap-2.5 py-1 font-normal",
            isByRule || isFixed ? "cursor-default" : "cursor-pointer",
          )}
        >
          {isPersonLens(lens) ? (
            <LicenseFeatureGroupReachIndicator
              state={reach ? "all" : "none"}
              title={describeReach({
                reaches: reach === true,
                status,
                userName: lens.userName,
                customerName: lens.customerName,
              })}
            />
          ) : isFixed && status && lens.kind === "customer" ? (
            <LicenseFeatureGroupIncludedMark
              title={describeIncluded(status, lens.customerName)}
            />
          ) : lens.kind === "package" ? (
            <LicenseFeatureGroupTickCheckbox
              meaning={tickMeaning}
              isCarriedHere={picked}
              disabled={isByRule}
              packageName={featureGroupName}
              rowName={nameOf(capability)}
              otherPackages={otherPackages}
              ariaLabel={`Grant ${nameOf(capability)}`}
              onToggle={(nextCarried) => {
                onSetPicked(
                  pickClosureOf(tree, capability, nextCarried),
                  nextCarried,
                )
              }}
            />
          ) : (
            <Checkbox
              checked={picked}
              disabled={isByRule}
              aria-label={`Add ${nameOf(capability)} for ${lens.customerName}`}
              onCheckedChange={(checked) => {
                onSetPicked(
                  pickClosureOf(tree, capability, checked === true),
                  checked === true,
                )
              }}
            />
          )}
          <LicenseFeatureGroupDetailsHoverCard
            title={nameOf(capability)}
            code={capability.Code}
            description={capability.Description}
            facts={capabilityFactsOf(capability, tree)}
            provenance={capability}
            inside={{
              surfaceCode: capability.Code,
              containedFeatureCount: capability.ContainedFeatureCount,
            }}
          >
            <span className="min-w-[5rem] flex-1 truncate text-xs">
              {nameOf(capability)}
            </span>
          </LicenseFeatureGroupDetailsHoverCard>
          {showsKind && (
            <span className={ROW_NOTE_CLASS}>
              {kindLabelOf(capability, parts.length > 0)}
            </span>
          )}
          {lens.kind === "package" && isByRule && (
            <span className={ROW_NOTE_CLASS}>{WHOLE_MODULE_NOUN}</span>
          )}
          {statusText && (
            <span className={ROW_NOTE_CLASS} title={statusText}>
              {statusText}
            </span>
          )}
          {isChanged && (
            <span className="shrink-0 text-[11px] text-primary">
              {picked
                ? PENDING_CHANGE_LABEL.added
                : PENDING_CHANGE_LABEL.removed}
            </span>
          )}
          {isOrphaned && parent && (
            <Badge size="xs" variant="warning">
              needs {nameOf(parent)}
            </Badge>
          )}
          {lens.kind === "package" && isLockable(capability) && (
            <LicenseFeatureGroupPackageOnlyMark />
          )}
          {alsoIn && (
            <span className={ROW_NOTE_CLASS} title={`Also in ${alsoIn}`}>
              also in {alsoIn}
            </span>
          )}
          {reach !== undefined &&
            lens.kind === "customer" &&
            !lens.isReadOnly && (
              <LicenseFeatureGroupReachMark
                reaches={reach}
                userName={lens.userName}
              />
            )}
        </Label>
        {parts.length > 0 && (
          <button
            type="button"
            className="ml-1 flex h-5 shrink-0 items-center gap-1 rounded px-1 text-[11px] text-muted-foreground hover:bg-muted hover:text-foreground"
            aria-expanded={isOpen}
            aria-label={
              isOpen
                ? `Hide what is inside ${nameOf(capability)}`
                : `Show what is inside ${nameOf(capability)}`
            }
            data-feature-code="UI_BackofficeFeatureGroup_Capabilities_RowToggle"
            onClick={() => {
              setIsOpen(!isOpen)
            }}
          >
            {!isOpen && pluralize(parts.length, "part")}
            <Icon src={isOpen ? "caret-down" : "caret-right"} size={11} />
          </button>
        )}
      </li>
      {isOpen &&
        parts.map((part) => (
          <LicenseFeatureGroupCapabilityRow
            key={part.Code}
            capability={part}
            tree={tree}
            depth={depth + 1}
            featureGroupName={featureGroupName}
            rules={rules}
            persistedRules={persistedRules}
            isPicked={isPicked}
            wasPicked={wasPicked}
            onSetPicked={onSetPicked}
          />
        ))}
    </>
  )
}

export default LicenseFeatureGroupCapabilityRow
